const PROFILES = {
    'pg-ident': {
        sep: '_',
        max: 31,
        re: /^[a-z][a-z0-9_]{0,30}$/,
        hint: 'Lowercase letters, digits and underscores; starts with a letter; up to 31 characters.',
    },
    'dns-label': {
        sep: '-',
        max: 63,
        re: /^[a-z][a-z0-9-]{0,62}$/,
        hint: 'Lowercase letters, digits and hyphens; starts with a letter; up to 63 characters.',
    },
};
// Characters allowed in a human DISPLAY NAME that derives an identifier: letters
// of any script (so "Crème Brûlée" is fine), digits, spaces, and a small set of
// ordinary name punctuation. Everything else — *, @, £, !, ", $, %, … — is
// REJECTED up-front instead of being silently scrubbed into the slug, so
// "my db-*@£!" is a mistake the user can see and fix, not a surprise database
// quietly named `my_db`. (No /g flag — used with .test per code point.)
const DISPLAY_NAME_CHAR = /[\p{L}\p{N} ._,&'()-]/u;
const DISPLAY_NAME_MAX = 120;
/** The punctuation we allow in a display name, for the rejection message. */
const DISPLAY_NAME_PUNCT = ". , & ' ( ) - _";
/** Strip combining accents after NFKD decomposition (é → e). */
function stripAccents(s) {
    return s.normalize('NFKD').replace(/[̀-ͯ]/g, '');
}
/** The rule for a profile — exposed so UIs can show consistent help text. */
export function identifierRule(profile) {
    return PROFILES[profile];
}
/**
 * Derive the canonical identifier for `name` under `profile`.
 *
 * Deterministic and idempotent — the same name always yields the same slug, and
 * normalizeIdentifier(normalizeIdentifier(x, p), p) === normalizeIdentifier(x, p).
 * "My database_1", "My Database 1" and "my-database-1" all collapse to the same
 * `my_database_1`, which is why two different-looking names can collide on the
 * derived slug (callers reject the collision and explain it).
 *
 * The returned string is NOT guaranteed valid — a name with no usable characters
 * yields '', and a digit-leading name yields a digit-leading slug. Run the result
 * through isValidIdentifier (or check it's non-empty) before using it. Keeping
 * derive and validate separate lets a UI render the live preview AND the reason
 * it's rejected from the same pair of pure functions.
 */
export function normalizeIdentifier(name, profile) {
    const { sep, max } = PROFILES[profile];
    const trimSep = new RegExp(`^\\${sep}+|\\${sep}+$`, 'g');
    const s = stripAccents(name)
        .toLowerCase()
        .replace(/[^a-z0-9]+/g, sep) // any run of non-alphanumerics → one separator
        .replace(trimSep, ''); // no leading/trailing separator
    // Cap length, then re-trim in case the cut landed on a separator.
    return s.slice(0, max).replace(new RegExp(`\\${sep}+$`), '');
}
/** True when `value` is a valid identifier for `profile` (matches its shape). */
export function isValidIdentifier(value, profile) {
    return PROFILES[profile].re.test(value);
}
/**
 * Turn a human display name into a system identifier, judging the INPUT as well
 * as the output. This is the entry point every "type a name, we derive the slug"
 * surface should call — one call validates the name, derives the slug, and
 * validates the slug, so a caller can't accidentally accept symbol-soup by
 * checking only the (always-clean, because normalize strips garbage) derived
 * form. "my db-*@£!" is rejected here, not quietly provisioned as `my_db`.
 *
 * Rejection order (first failure wins): empty → too long → illegal character →
 * unusable shape (e.g. a digit-leading name, which both Postgres and DNS forbid).
 */
export function deriveIdentifier(name, profile) {
    const trimmed = name.trim();
    if (trimmed === '')
        return { slug: '', ok: false, error: 'Enter a name.' };
    if (trimmed.length > DISPLAY_NAME_MAX) {
        return { slug: '', ok: false, error: `Keep the name under ${DISPLAY_NAME_MAX} characters.` };
    }
    // Reject symbol-soup up-front rather than scrubbing it into the slug. Array.from
    // walks code points (so an emoji counts once), and we de-duplicate so the
    // message lists each offending character only once.
    const illegal = [...new Set(Array.from(trimmed).filter((ch) => !DISPLAY_NAME_CHAR.test(ch)))];
    if (illegal.length > 0) {
        const chars = illegal.map((c) => `"${c}"`).join(' ');
        return {
            slug: '',
            ok: false,
            error: `Remove ${chars} — a name can use letters, numbers, spaces and ${DISPLAY_NAME_PUNCT}.`,
        };
    }
    const slug = normalizeIdentifier(trimmed, profile);
    if (!isValidIdentifier(slug, profile)) {
        return {
            slug,
            ok: false,
            error: `"${name}" can't be turned into an identifier. ${PROFILES[profile].hint}`,
        };
    }
    return { slug, ok: true };
}
/**
 * Live-filter for a field where the user types the identifier DIRECTLY (a schema
 * or table or column name), as opposed to deriving it from a display name. Use
 * this on `onChange`: it lowercases, maps spaces and the other separator to this
 * profile's separator, drops unsupported characters, and length-caps — but does
 * NOT trim a trailing separator, so "app_" survives on the way to "app_user".
 * (deriveIdentifier is the one to use when the user is typing a display NAME.)
 */
export function sanitizeIdentifierInput(raw, profile) {
    const { sep, max } = PROFILES[profile];
    const drop = sep === '_' ? /[^a-z0-9_]+/g : /[^a-z0-9-]+/g;
    return stripAccents(raw)
        .toLowerCase()
        .replace(/[\s_-]+/g, sep) // spaces + either separator → this profile's separator
        .replace(drop, '') // anything still unsupported is dropped
        .slice(0, max);
}
//# sourceMappingURL=identifier.js.map